import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";

import { useExecutiveMemoKnowledgeState } from "../hooks/useExecutiveMemoKnowledgeState";
import {
  api,
  type ExecutiveMemoDraftProcessData,
  type ExecutiveMemoListItem,
  type ExecutiveMemoPriority,
} from "../lib/api";
import { getOsCode } from "../lib/device";
import { AppError, logError, toUserMessage } from "../lib/errors";
import {
  EXECUTIVE_MEMO_ACCEPTED_IMAGE_TYPES,
  EXECUTIVE_MEMO_MAX_PAGES,
  createExecutiveMemoClientRequestId,
  dateTimeLocalToIso,
  formatExecutiveMemoDateTime,
  formatExecutiveMemoFailureMessage,
  formatExecutiveMemoKindLabel,
  formatExecutiveMemoPriorityLabel,
  getBackendMessage,
  getCurrentDateTimeLocalValue,
  getExecutiveMemoFileValidationMessage,
  parseCommaSeparatedValues,
  readPendingExecutiveMemoJobSnapshot,
  writePendingExecutiveMemoJobNotification,
  writePendingExecutiveMemoJobSnapshot,
} from "../lib/executiveMemos";
import {
  JOB_NOTIFICATION_EVENT,
  readPendingJobNotifications,
  type ExecutiveMemoJobNotification,
} from "../lib/jobNotifications";

type SortValue = "captured_at_desc" | "captured_at_asc" | "updated_at_desc";

type Filters = {
  q: string;
  kind: string;
  theme: string;
  tag: string;
  priority: ExecutiveMemoPriority[];
  from: string;
  to: string;
  sort: SortValue;
};

type PendingMemoRow = {
  jobId: string;
  status: "processing" | "failed" | "timed_out";
  failedError?: string;
  createdAt: string;
};

type SelectedPage = {
  localId: string;
  file: File;
  previewUrl: string;
  status: "pending" | "uploading" | "uploaded" | "failed";
  pageId?: string;
  errorMessage?: string;
};

function createLocalId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `local-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

type ExecutiveMemoWorkspaceLocationState = {
  openExecutiveMemoCreate?: boolean;
} | null;

const INITIAL_FILTERS: Filters = {
  q: "",
  kind: "",
  theme: "",
  tag: "",
  priority: [],
  from: "",
  to: "",
  sort: "captured_at_desc",
};

const HINT_MAX_LENGTH = 500;
const EXECUTIVE_MEMO_PROCESSING_BLOCK_MESSAGE =
  "前の社長メモを整理しています。完了してから次のメモを作成できます。";

function togglePriority(values: ExecutiveMemoPriority[], priority: ExecutiveMemoPriority) {
  return values.includes(priority)
    ? values.filter((value) => value !== priority)
    : [...values, priority];
}

function getMemoTitle(memo: ExecutiveMemoListItem) {
  return memo.title || memo.summary || memo.bodyPreview || "無題のメモ";
}

function isFiltered(f: Filters) {
  return (
    f.q !== "" ||
    f.kind !== "" ||
    f.theme !== "" ||
    f.tag !== "" ||
    f.priority.length > 0 ||
    f.from !== "" ||
    f.to !== ""
  );
}

function hasAdvancedFilters(f: Filters) {
  return (
    f.kind !== "" ||
    f.theme !== "" ||
    f.tag !== "" ||
    f.priority.length > 0 ||
    f.from !== "" ||
    f.to !== "" ||
    f.sort !== INITIAL_FILTERS.sort
  );
}

function upsertPendingRow(rows: PendingMemoRow[], row: PendingMemoRow) {
  const next = rows.filter((item) => item.jobId !== row.jobId);
  return [row, ...next].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
}

function PendingMemoCard({ row }: { row: PendingMemoRow }) {
  const failed = row.status === "failed" || row.status === "timed_out";
  const statusLabel = failed ? "処理に失敗" : "整理中";
  const disabledReason = failed
    ? "処理に失敗したため、AIで使うにはもう一度作成してください。"
    : "整理が完了するとAIで使えるようにできます。";

  return (
    <article className="box-border w-full max-w-full overflow-hidden rounded-2xl border border-[#EADBC8] bg-white p-4 shadow-sm">
      <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <h2 className="min-w-0 break-words text-base font-black text-[#5D4D46]">新しい社長メモ</h2>
            <span
              className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                failed ? "bg-[#FFF0EB] text-[#A1493E]" : "bg-[#F7F0DF] text-[#8A6E32]"
              }`}
            >
              {statusLabel}
            </span>
          </div>
          <p className="mt-2 break-words text-sm leading-6 text-[#6B5851]">
            {failed
              ? row.failedError || "画像を確認して、もう一度お試しください。"
              : "文字起こしと整理を進めています。完了するとこの一覧に反映されます。"}
          </p>
        </div>
        <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:min-w-[13rem]">
          <button
            type="button"
            disabled
            className="min-h-[42px] w-full rounded-full border border-[#D8C0AF] bg-[#F7F1EC] px-4 py-2 text-sm font-black text-[#8A746A] opacity-80"
          >
            AIで使えるようにする
          </button>
          <p className="break-words text-xs leading-5 text-[#8A746A]">{disabledReason}</p>
        </div>
      </div>
    </article>
  );
}

export default function ExecutiveMemosPage() {
  const location = useLocation();
  const locationState = location.state as ExecutiveMemoWorkspaceLocationState;
  const createRequested =
    Boolean(locationState?.openExecutiveMemoCreate) || new URLSearchParams(location.search).get("create") === "1";
  const appliedFiltersRef = useRef<Filters>(INITIAL_FILTERS);
  const libraryInputRef = useRef<HTMLInputElement | null>(null);
  const cameraInputRef = useRef<HTMLInputElement | null>(null);
  const knowledgeActionInFlightRef = useRef(new Set<string>());
  const draftIdRef = useRef<string | null>(null);
  const pagesRef = useRef<SelectedPage[]>([]);
  const uploadQueueRunningRef = useRef(false);
  // PCブラウザはinput[capture]を無視しファイル選択ダイアログを開くのみ（写真ライブラリと同じ動作）になるため、
  // 「写真を撮る」ボタンはカメラ付きOS（iOS/Android）でのみ表示する
  const canUseCameraCapture = useMemo(() => {
    const osCode = getOsCode();
    return osCode === 1 || osCode === 2;
  }, []);

  const [filters, setFilters] = useState<Filters>(INITIAL_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState<Filters>(INITIAL_FILTERS);
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(createRequested);
  const [items, setItems] = useState<ExecutiveMemoListItem[]>([]);
  const [pendingRows, setPendingRows] = useState<PendingMemoRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [pages, setPages] = useState<SelectedPage[]>([]);
  const [hint, setHint] = useState("");
  const [capturedAt, setCapturedAt] = useState(() => getCurrentDateTimeLocalValue());
  const [clientRequestId, setClientRequestId] = useState(() => createExecutiveMemoClientRequestId());
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [createErrorMessage, setCreateErrorMessage] = useState<string | null>(null);
  const [knowledgeActionLoadingId, setKnowledgeActionLoadingId] = useState<string | null>(null);
  const [knowledgeActionMessage, setKnowledgeActionMessage] = useState<string | null>(null);
  const [knowledgeActionError, setKnowledgeActionError] = useState<string | null>(null);
  const memoIds = useMemo(() => items.map((item) => item.id), [items]);
  const {
    status: knowledgeLookupStatus,
    errorMessage: knowledgeLookupErrorMessage,
    itemsByMemoId: knowledgeItemsByMemoId,
    rememberKnowledgeItem,
    reload: reloadKnowledgeState,
  } = useExecutiveMemoKnowledgeState(memoIds);

  const loadMemos = useCallback(
    async (targetFilters: Filters, cursor?: string) => {
      if (cursor) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setErrorMessage(null);

      try {
        const response = await api.listExecutiveMemos({
          q: targetFilters.q.trim() || undefined,
          kind: parseCommaSeparatedValues(targetFilters.kind),
          theme: parseCommaSeparatedValues(targetFilters.theme),
          tag: parseCommaSeparatedValues(targetFilters.tag),
          priority: targetFilters.priority,
          from: dateTimeLocalToIso(targetFilters.from ? `${targetFilters.from}T00:00` : ""),
          to: dateTimeLocalToIso(targetFilters.to ? `${targetFilters.to}T23:59` : ""),
          sort: targetFilters.sort,
          limit: 20,
          cursor,
        });

        setItems((prev) => (cursor ? [...prev, ...response.items] : response.items));
        setNextCursor(response.nextCursor ?? null);
      } catch (error) {
        logError(error);
        setErrorMessage(toUserMessage(error));
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    []
  );

  const revokeAllPreviews = useCallback((rows: SelectedPage[]) => {
    rows.forEach((row) => URL.revokeObjectURL(row.previewUrl));
  }, []);

  const resetCreateForm = useCallback(() => {
    revokeAllPreviews(pagesRef.current);
    pagesRef.current = [];
    setPages([]);
    draftIdRef.current = null;
    setHint("");
    setCapturedAt(getCurrentDateTimeLocalValue());
    setClientRequestId(createExecutiveMemoClientRequestId());
    setCreateErrorMessage(null);
    if (libraryInputRef.current) {
      libraryInputRef.current.value = "";
    }
    if (cameraInputRef.current) {
      cameraInputRef.current.value = "";
    }
  }, [revokeAllPreviews]);

  // 画面離脱・アンマウント時にobjectURLを解放する
  useEffect(() => {
    return () => {
      revokeAllPreviews(pagesRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // pagesRefを唯一の同期的な真実の情報源として即時更新してからsetPagesへ渡す。
  // setPagesのfunctional updater内でpagesRefを更新すると、Reactのバッチ処理タイミング次第で
  // 反映が遅延し、runUploadQueue()のwhileループが同じページを無限に処理し続ける恐れがある。
  const updatePage = useCallback((localId: string, patch: Partial<SelectedPage>) => {
    const next = pagesRef.current.map((row) => (row.localId === localId ? { ...row, ...patch } : row));
    pagesRef.current = next;
    setPages(next);
  }, []);

  const ensureDraft = useCallback(async () => {
    if (draftIdRef.current) return draftIdRef.current;
    const draft = await api.createExecutiveMemoDraft();
    draftIdRef.current = draft.draftId;
    return draft.draftId;
  }, []);

  const uploadOnePage = useCallback(
    async (localId: string) => {
      const pageIndex = pagesRef.current.findIndex((row) => row.localId === localId);
      if (pageIndex === -1) return;
      const page = pagesRef.current[pageIndex];

      updatePage(localId, { status: "uploading", errorMessage: undefined });

      try {
        const draftId = await ensureDraft();

        // 前回失敗した際に発行済みのページ行が残っていれば、先に削除してから再発行する
        // （同じpage_noで再アップロードするため、unique制約に抵触しないようにする）。
        if (page.pageId) {
          try {
            await api.deleteExecutiveMemoPage(draftId, page.pageId);
          } catch {
            // 既に存在しない等は無視して続行する
          }
        }

        const pageNo = pageIndex + 1;
        const uploadUrlData = await api.createExecutiveMemoPageUploadUrl({
          draftId,
          pageNo,
          filename: page.file.name,
          mimeType: page.file.type || "application/octet-stream",
          sizeBytes: page.file.size,
        });

        updatePage(localId, { pageId: uploadUrlData.pageId });

        const safeUploadHeaders = Object.fromEntries(
          Object.entries(uploadUrlData.uploadHeaders ?? {}).filter(
            ([key]) => key.toLowerCase() !== "authorization"
          )
        );

        const putResponse = await fetch(uploadUrlData.uploadUrl, {
          method: "PUT",
          headers: {
            ...safeUploadHeaders,
            "Content-Type": page.file.type || "application/octet-stream",
          },
          body: page.file,
        });
        if (!putResponse.ok) {
          throw new Error("画像のアップロードに失敗しました。");
        }

        await api.completeExecutiveMemoPageUpload(draftId, uploadUrlData.pageId);
        updatePage(localId, { status: "uploaded", errorMessage: undefined });
      } catch (error) {
        logError(error);
        updatePage(localId, {
          status: "failed",
          errorMessage: formatExecutiveMemoFailureMessage(
            getBackendMessage(error) ?? toUserMessage(error)
          ),
        });
      }
    },
    [ensureDraft, updatePage]
  );

  const runUploadQueue = useCallback(async () => {
    if (uploadQueueRunningRef.current) return;
    uploadQueueRunningRef.current = true;

    try {
      // 並行アップロードによるpage_no採番の競合を避けるため、1件ずつ順番に処理する。
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const next = pagesRef.current.find((row) => row.status === "pending");
        if (!next) break;
        await uploadOnePage(next.localId);
      }
    } finally {
      uploadQueueRunningRef.current = false;
    }
  }, [uploadOnePage]);

  const addFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;

      const room = EXECUTIVE_MEMO_MAX_PAGES - pagesRef.current.length;
      if (room <= 0) {
        setCreateErrorMessage(`社長メモ1件につき画像は${EXECUTIVE_MEMO_MAX_PAGES}枚までです。`);
        return;
      }

      const accepted: SelectedPage[] = [];
      for (const file of files.slice(0, room)) {
        const validationMessage = getExecutiveMemoFileValidationMessage(file);
        if (validationMessage) {
          setCreateErrorMessage(validationMessage);
          continue;
        }
        accepted.push({
          localId: createLocalId(),
          file,
          previewUrl: URL.createObjectURL(file),
          status: "pending",
        });
      }

      if (files.length > room) {
        setCreateErrorMessage(`社長メモ1件につき画像は${EXECUTIVE_MEMO_MAX_PAGES}枚までです。`);
      } else if (accepted.length > 0) {
        setCreateErrorMessage(null);
      }

      if (accepted.length === 0) return;

      setCapturedAt((current) => current || getCurrentDateTimeLocalValue());
      pagesRef.current = [...pagesRef.current, ...accepted];
      setPages(pagesRef.current);
      void runUploadQueue();
    },
    [runUploadQueue]
  );

  const handleLibraryFilesSelected: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const files = Array.from(event.target.files ?? []);
    addFiles(files);
    event.target.value = "";
  };

  const handleCameraFileSelected: React.ChangeEventHandler<HTMLInputElement> = (event) => {
    const files = Array.from(event.target.files ?? []);
    addFiles(files);
    event.target.value = "";
  };

  const handleRetryPage = (localId: string) => {
    void uploadOnePage(localId);
  };

  const handleDeletePage = async (localId: string) => {
    const page = pagesRef.current.find((row) => row.localId === localId);
    if (!page) return;

    if (page.pageId && draftIdRef.current) {
      try {
        await api.deleteExecutiveMemoPage(draftIdRef.current, page.pageId);
      } catch (error) {
        logError(error);
        setCreateErrorMessage(toUserMessage(error));
        return;
      }
    }

    URL.revokeObjectURL(page.previewUrl);
    pagesRef.current = pagesRef.current.filter((row) => row.localId !== localId);
    setPages(pagesRef.current);
  };

  const canReorder = pages.length > 1 && pages.every((row) => row.status === "uploaded");

  const handleMovePage = async (localId: string, direction: -1 | 1) => {
    if (!canReorder) return;
    const index = pagesRef.current.findIndex((row) => row.localId === localId);
    const targetIndex = index + direction;
    if (index === -1 || targetIndex < 0 || targetIndex >= pagesRef.current.length) return;

    const next = [...pagesRef.current];
    [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
    pagesRef.current = next;
    setPages(next);

    if (draftIdRef.current) {
      try {
        await api.reorderExecutiveMemoPages(
          draftIdRef.current,
          next.map((row) => row.pageId).filter((id): id is string => Boolean(id))
        );
      } catch (error) {
        logError(error);
        setCreateErrorMessage(toUserMessage(error));
      }
    }
  };

  const addPendingRowFromCreate = useCallback(
    (response: ExecutiveMemoDraftProcessData, requestId: string) => {
      const acceptedAtMs = Date.parse(response.acceptedAt);
      const startedAtMs = Number.isFinite(acceptedAtMs) ? acceptedAtMs : Date.now();
      const startedAt = new Date(startedAtMs).toISOString();

      writePendingExecutiveMemoJobSnapshot({
        jobId: response.jobId,
        clientRequestId: requestId,
        startedAt,
      });
      writePendingExecutiveMemoJobNotification({
        jobId: response.jobId,
        status: "processing",
      });
      setPendingRows((prev) =>
        upsertPendingRow(prev, {
          jobId: response.jobId,
          status: "processing",
          createdAt: startedAt,
        })
      );
    },
    []
  );

  const syncPendingRowsFromNotifications = useCallback(() => {
    const notifications = readPendingJobNotifications("executive_memo") as ExecutiveMemoJobNotification[];
    const completed = notifications.some((notification) => notification.status === "completed");

    setPendingRows((prev) => {
      let next = [...prev];

      for (const notification of notifications) {
        if (notification.status === "processing") {
          next = upsertPendingRow(next, {
            jobId: notification.jobId,
            status: "processing",
            createdAt: notification.processedAt ?? new Date().toISOString(),
          });
        } else if (notification.status === "failed" || notification.status === "timed_out") {
          next = upsertPendingRow(next, {
            jobId: notification.jobId,
            status: notification.status,
            failedError: notification.failedError
              ? formatExecutiveMemoFailureMessage(notification.failedError)
              : undefined,
            createdAt: notification.processedAt ?? new Date().toISOString(),
          });
        } else if (notification.status === "completed") {
          next = next.filter((row) => row.jobId !== notification.jobId);
        }
      }

      return next;
    });

    if (completed) {
      void loadMemos(appliedFiltersRef.current);
    }
  }, [loadMemos]);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    void loadMemos(INITIAL_FILTERS);
  }, [loadMemos]);

  useEffect(() => {
    if (!createRequested) return;

    setCreateOpen(true);
  }, [createRequested]);

  useEffect(() => {
    const pendingSnapshot = readPendingExecutiveMemoJobSnapshot();
    if (pendingSnapshot?.jobId) {
      setPendingRows((prev) =>
        upsertPendingRow(prev, {
          jobId: pendingSnapshot.jobId,
          status: "processing",
          createdAt: pendingSnapshot.startedAt,
        })
      );
    }

    syncPendingRowsFromNotifications();

    const handleNotificationEvent = () => {
      syncPendingRowsFromNotifications();
    };

    window.addEventListener(JOB_NOTIFICATION_EVENT, handleNotificationEvent as EventListener);

    return () => {
      window.removeEventListener(JOB_NOTIFICATION_EVENT, handleNotificationEvent as EventListener);
    };
  }, [syncPendingRowsFromNotifications]);

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    void loadMemos(filters);
  };

  const handleClearFilters = () => {
    setFilters(INITIAL_FILTERS);
    setAppliedFilters(INITIAL_FILTERS);
    void loadMemos(INITIAL_FILTERS);
  };

  const hasProcessingPendingRow = pendingRows.some((row) => row.status === "processing");

  const handleOpenCreate = () => {
    if (hasProcessingPendingRow) return;

    setCreateOpen(true);
    setCreateErrorMessage(null);
    setCapturedAt((current) => current || getCurrentDateTimeLocalValue());
  };

  const handleCloseCreate = () => {
    if (createSubmitting) return;

    setCreateOpen(false);
    setCreateErrorMessage(null);
  };

  const openLibraryPicker = () => {
    libraryInputRef.current?.click();
  };

  const openCameraPicker = () => {
    cameraInputRef.current?.click();
  };

  const hasUploadingPage = pages.some((row) => row.status === "pending" || row.status === "uploading");
  const hasFailedPage = pages.some((row) => row.status === "failed");
  const uploadedPageCount = pages.filter((row) => row.status === "uploaded").length;
  const canSubmitCreate = pages.length > 0 && !hasUploadingPage && !hasFailedPage;

  const handleSubmitCreate = async () => {
    if (createSubmitting || hasProcessingPendingRow) return;

    if (pages.length === 0) {
      setCreateErrorMessage("写真を1枚以上追加してください。");
      return;
    }

    if (hasUploadingPage) {
      setCreateErrorMessage("画像のアップロードが完了してから送信してください。");
      return;
    }

    if (hasFailedPage) {
      setCreateErrorMessage("アップロードに失敗した画像があります。再試行するか削除してください。");
      return;
    }

    if (!capturedAt.trim()) {
      setCreateErrorMessage("撮影日時を入力してください。");
      return;
    }

    const capturedAtIso = dateTimeLocalToIso(capturedAt);
    if (!capturedAtIso) {
      setCreateErrorMessage("撮影日時を正しく入力してください。");
      return;
    }

    const draftId = draftIdRef.current;
    if (!draftId) {
      setCreateErrorMessage("写真を1枚以上追加してください。");
      return;
    }

    setCreateSubmitting(true);
    setCreateErrorMessage(null);

    try {
      const requestId = clientRequestId;
      const response = await api.processExecutiveMemoDraft(draftId, {
        clientRequestId: requestId,
        capturedAt: capturedAtIso,
        hint: hint.trim() || undefined,
      });

      if (response.relayStatus !== "accepted") {
        // dispatchに失敗している状態。draft/pagesはそのまま残し、フォームを閉じずに再試行できるようにする。
        setCreateErrorMessage(
          "エージェントへの処理依頼に失敗しました。もう一度「社長メモを作成する」をお試しください。"
        );
        return;
      }

      addPendingRowFromCreate(response, requestId);
      resetCreateForm();
      setCreateOpen(false);
    } catch (error) {
      logError(error);
      if (error instanceof AppError && error.meta.status === 429) {
        setCreateErrorMessage("リクエストが集中しています。少し時間をおいて再試行してください。");
      } else if (error instanceof AppError && error.code === "CONFLICT") {
        setCreateErrorMessage(
          formatExecutiveMemoFailureMessage(
            getBackendMessage(error, ["client_request_id"]) ?? "入力内容を確認して再依頼してください。"
          )
        );
      } else {
        setCreateErrorMessage(
          formatExecutiveMemoFailureMessage(
            getBackendMessage(error, ["hint", "captured_at"]) ?? toUserMessage(error)
          )
        );
      }
    } finally {
      setCreateSubmitting(false);
    }
  };

  const handleAddToKnowledgeLibrary = async (memo: ExecutiveMemoListItem) => {
    if (knowledgeActionInFlightRef.current.has(memo.id)) return;

    knowledgeActionInFlightRef.current.add(memo.id);
    setKnowledgeActionLoadingId(memo.id);
    setKnowledgeActionMessage(null);
    setKnowledgeActionError(null);

    try {
      const response = await api.createKnowledgeItemFromExecutiveMemo(memo.id);
      setKnowledgeActionMessage("ナレッジライブラリに追加・更新しました。壁打ちの参考資料として選べます。");
      rememberKnowledgeItem(response);
    } catch (error) {
      logError(error);
      setKnowledgeActionError(
        `${toUserMessage(error)} ナレッジライブラリに追加できませんでした。通信状況を確認して、もう一度お試しください。`
      );
    } finally {
      knowledgeActionInFlightRef.current.delete(memo.id);
      setKnowledgeActionLoadingId((current) => (current === memo.id ? null : current));
    }
  };

  const filtered = isFiltered(appliedFilters);
  const advancedActive = hasAdvancedFilters(filters);
  const hasPendingRows = pendingRows.length > 0;
  const hasListRows = items.length > 0 || hasPendingRows;

  return (
    <main className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto box-border w-full max-w-2xl min-w-0">
        <header className="mb-6 flex min-w-0 items-center justify-between gap-3">
          <Link to="/request" className="shrink-0 text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]">
            ←戻る
          </Link>
          <p className="min-w-0 truncate text-sm font-bold tracking-tight text-[#7C5A4E]">社長メモ</p>
          <Link to="/home" className="shrink-0 text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="box-border w-full max-w-full rounded-[2rem] border border-white/80 bg-white/90 p-4 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-6">
          <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <p className="break-words text-xs font-black tracking-[0.12em] text-[#A67C52]">社長メモ管理</p>
              <h1 className="mt-3 break-words text-2xl font-black tracking-tight text-[#7C5A4E]">社長メモ</h1>
              <p className="mt-2 break-words text-sm leading-6 text-[#8A746A]">
                新しいメモ作成、検索、一覧確認をこの画面でまとめて行えます。
              </p>
            </div>
            <button
              type="button"
              onClick={createOpen ? handleCloseCreate : handleOpenCreate}
              disabled={!createOpen && hasProcessingPendingRow}
              aria-expanded={createOpen}
              className="inline-flex min-h-[44px] w-full shrink-0 items-center justify-center rounded-full bg-[#7C5A4E] px-4 py-2 text-sm font-black text-white transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
            >
              {createOpen ? "作成フォームを閉じる" : "新しい社長メモを作る"}
            </button>
          </div>

          {hasProcessingPendingRow ? (
            <p className="mt-3 break-words rounded-2xl border border-[#E8D5C8] bg-[#FFF8ED] px-4 py-3 text-sm font-bold text-[#8A6E32]">
              {EXECUTIVE_MEMO_PROCESSING_BLOCK_MESSAGE}
            </p>
          ) : null}

          {createOpen ? (
            <div
              data-testid="executive-memo-create-panel"
              className="mt-4 box-border w-full max-w-full rounded-[1.5rem] border border-[#E4D5C8] bg-white px-4 py-4 sm:px-5"
            >
              <div className="grid min-w-0 gap-4">
                <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="break-words text-sm font-black text-[#5D4D46]">写真を追加</p>
                    <p className="mt-1 break-words text-sm leading-6 text-[#8A746A]">
                      対応形式: JPEG / PNG / WEBP / HEIC / HEIF（1枚10MBまで）
                    </p>
                    <p className="break-words text-sm leading-6 text-[#8A746A]">
                      複数ページの資料やノートは、写真を{EXECUTIVE_MEMO_MAX_PAGES}枚まで1つの社長メモにまとめられます。
                    </p>
                  </div>
                  <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row">
                    {canUseCameraCapture ? (
                      <button
                        type="button"
                        onClick={openCameraPicker}
                        disabled={createSubmitting || pages.length >= EXECUTIVE_MEMO_MAX_PAGES}
                        className="inline-flex min-h-[44px] w-full items-center justify-center rounded-full bg-[#7C5A4E] px-5 py-3 text-sm font-black text-white transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                      >
                        写真を撮る
                      </button>
                    ) : null}
                    <button
                      type="button"
                      onClick={openLibraryPicker}
                      disabled={createSubmitting || pages.length >= EXECUTIVE_MEMO_MAX_PAGES}
                      className="inline-flex min-h-[44px] w-full items-center justify-center rounded-full border border-[#7C5A4E] bg-white px-5 py-3 text-sm font-black text-[#7C5A4E] transition hover:bg-[#F5EDE8] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                    >
                      写真ライブラリから選ぶ
                    </button>
                  </div>
                </div>
                {canUseCameraCapture ? (
                  <input
                    ref={cameraInputRef}
                    type="file"
                    accept={EXECUTIVE_MEMO_ACCEPTED_IMAGE_TYPES.join(",")}
                    capture="environment"
                    onChange={handleCameraFileSelected}
                    disabled={createSubmitting}
                    className="hidden"
                  />
                ) : null}
                <input
                  ref={libraryInputRef}
                  type="file"
                  accept={EXECUTIVE_MEMO_ACCEPTED_IMAGE_TYPES.join(",")}
                  multiple
                  onChange={handleLibraryFilesSelected}
                  disabled={createSubmitting}
                  className="hidden"
                />

                {pages.length > 0 ? (
                  <div className="min-w-0">
                    <p className="mb-2 text-xs font-bold text-[#7C5A4E]">
                      {uploadedPageCount}/{pages.length}枚アップロード完了
                    </p>
                    <ul className="grid min-w-0 grid-cols-2 gap-3 sm:grid-cols-3">
                      {pages.map((page, index) => (
                        <li
                          key={page.localId}
                          className="relative box-border overflow-hidden rounded-xl border border-[#EADBC8] bg-white"
                        >
                          <span className="absolute left-1.5 top-1.5 z-10 rounded-full bg-black/60 px-2 py-0.5 text-xs font-bold text-white">
                            {index + 1}
                          </span>
                          <img
                            src={page.previewUrl}
                            alt={`ページ${index + 1}のプレビュー`}
                            className="aspect-square w-full object-cover"
                          />
                          {page.status === "uploading" || page.status === "pending" ? (
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 text-xs font-bold text-white">
                              アップロード中...
                            </div>
                          ) : null}
                          {page.status === "failed" ? (
                            <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-black/60 p-2 text-center text-xs font-bold text-white">
                              <span className="break-words">{page.errorMessage || "アップロード失敗"}</span>
                              <button
                                type="button"
                                onClick={() => handleRetryPage(page.localId)}
                                className="mt-1 rounded-full bg-white px-3 py-1 text-xs font-black text-[#A1493E]"
                              >
                                再試行
                              </button>
                            </div>
                          ) : null}
                          <div className="flex items-center justify-between gap-1 bg-[#FFFAF7] px-1.5 py-1">
                            <button
                              type="button"
                              aria-label={`${index + 1}番目の写真を1つ上へ`}
                              onClick={() => void handleMovePage(page.localId, -1)}
                              disabled={!canReorder || index === 0 || createSubmitting}
                              className="min-h-[32px] min-w-[32px] rounded-full text-sm font-black text-[#7C5A4E] disabled:cursor-not-allowed disabled:opacity-30"
                            >
                              ↑
                            </button>
                            <button
                              type="button"
                              aria-label={`${index + 1}番目の写真を1つ下へ`}
                              onClick={() => void handleMovePage(page.localId, 1)}
                              disabled={!canReorder || index === pages.length - 1 || createSubmitting}
                              className="min-h-[32px] min-w-[32px] rounded-full text-sm font-black text-[#7C5A4E] disabled:cursor-not-allowed disabled:opacity-30"
                            >
                              ↓
                            </button>
                            <button
                              type="button"
                              aria-label={`${index + 1}番目の写真を削除`}
                              onClick={() => void handleDeletePage(page.localId)}
                              disabled={createSubmitting || page.status === "uploading"}
                              className="min-h-[32px] min-w-[32px] rounded-full text-sm font-black text-[#A1493E] disabled:cursor-not-allowed disabled:opacity-30"
                            >
                              削除
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <label className="block min-w-0">
                  <span className="mb-2 block text-sm font-bold text-[#5D4D46]">
                    ヒント <span className="font-normal text-[#8A746A]">任意</span>
                  </span>
                  <textarea
                    value={hint}
                    onChange={(event) => setHint(event.target.value.slice(0, HINT_MAX_LENGTH))}
                    disabled={createSubmitting}
                    maxLength={HINT_MAX_LENGTH}
                    rows={3}
                    className="block w-full min-w-0 max-w-full resize-y rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm leading-6 focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20 disabled:cursor-not-allowed disabled:opacity-60"
                  />
                  <span className="mt-2 block text-right text-xs text-[#8A746A]">
                    {hint.length}/{HINT_MAX_LENGTH}
                  </span>
                </label>

                <label className="block min-w-0">
                  <span className="mb-2 block text-sm font-bold text-[#5D4D46]">撮影日時</span>
                  <input
                    type="datetime-local"
                    value={capturedAt}
                    onChange={(event) => setCapturedAt(event.target.value)}
                    disabled={createSubmitting}
                    required
                    className="block w-full min-w-0 max-w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm focus:outline-none disabled:cursor-not-allowed disabled:opacity-60"
                  />
                </label>

                <div className="flex min-w-0 flex-col gap-3 border-t border-[#F0E4DA] pt-4 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleSubmitCreate}
                    disabled={createSubmitting || !canSubmitCreate || hasProcessingPendingRow}
                    className="min-h-[44px] w-full rounded-full bg-[#7C5A4E] px-5 py-3 text-sm font-black text-white shadow-[0_12px_28px_rgba(124,90,78,0.16)] transition hover:bg-[#5D4D46] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:flex-1"
                  >
                    {createSubmitting ? "送信中..." : "社長メモを作成する"}
                  </button>
                  <button
                    type="button"
                    onClick={resetCreateForm}
                    disabled={createSubmitting}
                    className="min-h-[44px] w-full rounded-full border border-[#D8C0AF] bg-white px-5 py-3 text-sm font-bold text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto"
                  >
                    入力をクリア
                  </button>
                </div>
              </div>

              {createErrorMessage ? (
                <div role="alert" className="mt-4 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
                  {createErrorMessage}
                </div>
              ) : null}
            </div>
          ) : null}

          <div className="mt-4 box-border w-full max-w-full rounded-2xl border border-[#E8D5C8] bg-[#FFFAF7] p-4">
            <div className="grid min-w-0 gap-3">
              <div className="min-w-0">
                <label htmlFor="executive-memo-keyword" className="mb-1 block text-xs font-bold text-[#7C5A4E]">キーワード検索</label>
                <input
                  id="executive-memo-keyword"
                  value={filters.q}
                  maxLength={200}
                  onChange={(event) => setFilters((prev) => ({ ...prev, q: event.target.value }))}
                  className="w-full min-w-0 max-w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
                />
              </div>
              <div className="flex min-w-0 flex-col gap-2 sm:flex-row">
                <button
                  type="button"
                  onClick={handleApplyFilters}
                  disabled={loading}
                  className="min-h-[42px] rounded-full bg-[#A67C52] px-5 py-2.5 text-sm font-black text-white hover:bg-[#7C5A4E] disabled:opacity-50"
                >
                  検索する
                </button>
                <button
                  type="button"
                  onClick={() => setAdvancedFiltersOpen((value) => !value)}
                  aria-expanded={advancedFiltersOpen}
                  className="min-h-[42px] rounded-full border border-[#D8C0AF] bg-white px-5 py-2.5 text-sm font-black text-[#7C5A4E] hover:bg-[#F5EDE8]"
                >
                  {advancedFiltersOpen ? "詳細条件を閉じる" : "詳細条件を開く"}
                  {advancedActive ? <span className="ml-2 text-xs text-[#A1493E]">条件あり</span> : null}
                </button>
              </div>
            </div>

            {advancedFiltersOpen ? (
              <div className="mt-4 border-t border-[#E8D5C8] pt-4">
                <p className="mb-3 text-xs font-black tracking-wide text-[#A67C52]">詳細条件</p>
                <div className="grid min-w-0 gap-3 sm:grid-cols-2">
                  <div className="min-w-0">
                    <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">種別</label>
                    <input
                      value={filters.kind}
                      onChange={(event) => setFilters((prev) => ({ ...prev, kind: event.target.value }))}
                      placeholder="戦略, 商談"
                      className="w-full min-w-0 max-w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">テーマ</label>
                    <input
                      value={filters.theme}
                      onChange={(event) => setFilters((prev) => ({ ...prev, theme: event.target.value }))}
                      placeholder="相談, 調査"
                      className="w-full min-w-0 max-w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">タグ</label>
                    <input
                      value={filters.tag}
                      onChange={(event) => setFilters((prev) => ({ ...prev, tag: event.target.value }))}
                      placeholder="顧客A, 採用"
                      className="w-full min-w-0 max-w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none focus:ring-2 focus:ring-[#A67C52]/40"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">並び順</label>
                    <select
                      value={filters.sort}
                      onChange={(event) => setFilters((prev) => ({ ...prev, sort: event.target.value as SortValue }))}
                      className="w-full min-w-0 max-w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none"
                    >
                      <option value="captured_at_desc">撮影日が新しい順</option>
                      <option value="captured_at_asc">撮影日が古い順</option>
                      <option value="updated_at_desc">更新日が新しい順</option>
                    </select>
                  </div>
                  <div className="min-w-0">
                    <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">撮影日（開始）</label>
                    <input
                      type="date"
                      value={filters.from}
                      onChange={(event) => setFilters((prev) => ({ ...prev, from: event.target.value }))}
                      className="w-full min-w-0 max-w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none"
                    />
                  </div>
                  <div className="min-w-0">
                    <label className="mb-1 block text-xs font-bold text-[#7C5A4E]">撮影日（終了）</label>
                    <input
                      type="date"
                      value={filters.to}
                      onChange={(event) => setFilters((prev) => ({ ...prev, to: event.target.value }))}
                      className="w-full min-w-0 max-w-full rounded-xl border border-[#E8D5C8] bg-white px-3 py-2 text-sm text-[#4A3E39] focus:outline-none"
                    />
                  </div>
                </div>
                <div className="mt-3">
                  <p className="mb-2 text-xs font-bold text-[#7C5A4E]">優先度</p>
                  <div className="flex min-w-0 flex-wrap gap-4">
                    {(["high", "mid", "low"] as ExecutiveMemoPriority[]).map((priority) => (
                      <label key={priority} className="inline-flex cursor-pointer items-center gap-1.5 text-sm font-bold text-[#5D4D46]">
                        <input
                          type="checkbox"
                          checked={filters.priority.includes(priority)}
                          onChange={() =>
                            setFilters((prev) => ({
                              ...prev,
                              priority: togglePriority(prev.priority, priority),
                            }))
                          }
                          className="accent-[#A67C52]"
                        />
                        {formatExecutiveMemoPriorityLabel(priority)}
                      </label>
                    ))}
                  </div>
                </div>
                <div className="mt-3 flex min-w-0 flex-col gap-2 sm:flex-row">
                  <button
                    type="button"
                    onClick={handleApplyFilters}
                    disabled={loading}
                    className="min-h-[42px] flex-1 rounded-full bg-[#A67C52] py-2 text-sm font-black text-white hover:bg-[#7C5A4E] disabled:opacity-50"
                  >
                    絞り込む
                  </button>
                  <button
                    type="button"
                    onClick={handleClearFilters}
                    disabled={loading}
                    className="min-h-[42px] rounded-full border border-[#A67C52] px-4 py-2 text-sm font-black text-[#A67C52] hover:bg-[#F5EDE8] disabled:opacity-50"
                  >
                    リセット
                  </button>
                </div>
              </div>
            ) : null}
          </div>

          {!loading && errorMessage && (
            <p className="mt-4 break-words rounded-2xl bg-[#FFF0EB] p-3 text-sm font-bold text-[#B4533A]">{errorMessage}</p>
          )}
          {knowledgeActionMessage ? (
            <p className="mt-4 break-words rounded-2xl border border-[#D8E5D2] bg-[#F4FAF1] px-4 py-3 text-sm font-bold text-[#3F5C3B]">
              {knowledgeActionMessage}
            </p>
          ) : null}
          {knowledgeActionError ? (
            <p role="alert" className="mt-4 break-words rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm font-bold text-[#A1493E]">
              {knowledgeActionError}
            </p>
          ) : null}
          {knowledgeLookupStatus === "error" ? (
            <div role="alert" className="mt-4 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
              <p className="break-words font-bold">
                ナレッジライブラリの状態を確認できませんでした。ページを再読み込みするか、もう一度お試しください。
              </p>
              {knowledgeLookupErrorMessage ? <p className="mt-1 break-words text-xs">{knowledgeLookupErrorMessage}</p> : null}
              <button
                type="button"
                onClick={reloadKnowledgeState}
                className="mt-3 min-h-[36px] rounded-full border border-[#C9867D] bg-white px-4 py-1.5 text-xs font-black text-[#A1493E] transition hover:bg-[#FFF0EE]"
              >
                状態を再確認する
              </button>
            </div>
          ) : null}
        </section>

        <section
          aria-label="社長メモ一覧"
          className="mt-4 box-border w-full max-w-full rounded-[2rem] border border-[#EADBC8] bg-white/80 p-4 shadow-[0_18px_48px_rgba(124,90,78,0.12)] sm:p-5"
        >
          {!loading && hasListRows && (
            <p className="mb-3 break-words text-xs text-[#9C867D]">
              {filtered ? "絞り込み結果：" : "全"}
              {items.length + pendingRows.length}件{nextCursor ? "以上" : ""}
            </p>
          )}

          {loading ? (
            <p className="py-10 text-center text-sm text-[#9C867D]">読み込み中...</p>
          ) : !errorMessage && !hasListRows ? (
            <div className="flex min-h-[220px] items-center justify-center rounded-2xl border border-dashed border-[#EADBC8] bg-[#FFFCFA] p-5 text-center text-sm leading-7 text-[#6B5851]">
              {filtered ? "条件に一致するメモがありません。" : "まだ社長メモがありません。最初の1枚を撮ってみましょう。"}
            </div>
          ) : (
            <div className="grid min-w-0 gap-3">
              {pendingRows.map((row) => (
                <PendingMemoCard key={row.jobId} row={row} />
              ))}

              {items.map((memo) => {
                const linkedKnowledgeItem = knowledgeItemsByMemoId[memo.id] ?? null;
                const knowledgeLinked = Boolean(linkedKnowledgeItem);
                const knowledgeLoading = knowledgeActionLoadingId === memo.id;
                const knowledgeChecking = knowledgeLookupStatus === "loading";
                const knowledgeLookupFailed = knowledgeLookupStatus === "error";

                return (
                  <article key={memo.id} className="box-border w-full max-w-full overflow-hidden rounded-2xl border border-[#EADBC8] bg-white p-4 shadow-sm">
                    <div className="flex min-w-0 flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 flex-wrap items-center gap-2">
                          <h2 className="min-w-0 break-words text-base font-black text-[#5D4D46]">{getMemoTitle(memo)}</h2>
                          <span className="rounded-full bg-[#F4FAF1] px-2.5 py-1 text-xs font-bold text-[#3F5C3B]">利用可能</span>
                        </div>
                        <p className="mt-2 line-clamp-2 break-words text-sm leading-6 text-[#6B5851]">
                          {memo.bodyPreview || memo.summary}
                        </p>
                        <div className="mt-3 flex min-w-0 flex-wrap gap-2">
                          {memo.kind ? <span className="rounded-full bg-[#F2EDE5] px-2.5 py-1 text-xs font-bold text-[#7C5A4E]">{formatExecutiveMemoKindLabel(memo.kind)}</span> : null}
                          {memo.themes.slice(0, 3).map((theme) => (
                            <span key={theme} className="max-w-full break-words rounded-full bg-[#F4E8DC] px-2.5 py-1 text-xs font-bold text-[#A67C52]">
                              {theme}
                            </span>
                          ))}
                          {memo.themes.length > 3 ? (
                            <span className="rounded-full bg-[#F4E8DC] px-2.5 py-1 text-xs font-bold text-[#A67C52]">
                              +{memo.themes.length - 3}
                            </span>
                          ) : null}
                          {memo.tags.map((tag) => (
                            <span key={tag} className="max-w-full break-words rounded-full bg-[#EFF3F8] px-2.5 py-1 text-xs font-bold text-[#5D7080]">
                              {tag}
                            </span>
                          ))}
                        </div>
                        <div className="mt-2 flex min-w-0 flex-wrap gap-x-2 gap-y-1 text-xs text-[#8A746A]">
                          <span className="font-bold text-[#7C5A4E]">優先度 {formatExecutiveMemoPriorityLabel(memo.priority)}</span>
                          <span>{formatExecutiveMemoDateTime(memo.capturedAt)}</span>
                        </div>
                      </div>
                      <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:min-w-[13rem]">
                        {knowledgeLinked ? (
                          <Link
                            to="/request/knowledge"
                            className="min-h-[42px] w-full rounded-full bg-[#4F714B] px-4 py-2 text-center text-sm font-black text-white shadow-sm transition hover:bg-[#3F5C3B]"
                          >
                            ナレッジライブラリで見る
                          </Link>
                        ) : knowledgeChecking ? (
                          <button
                            type="button"
                            disabled
                            className="min-h-[42px] w-full rounded-full bg-[#4F714B] px-4 py-2 text-sm font-black text-white shadow-sm opacity-60"
                          >
                            確認中
                          </button>
                        ) : knowledgeLookupFailed ? (
                          <button
                            type="button"
                            disabled
                            className="min-h-[42px] w-full rounded-full bg-[#F7F1EC] px-4 py-2 text-sm font-black text-[#8A746A] opacity-80"
                          >
                            状態を確認できません
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => void handleAddToKnowledgeLibrary(memo)}
                            disabled={knowledgeLoading}
                            className="min-h-[42px] w-full rounded-full bg-[#4F714B] px-4 py-2 text-sm font-black text-white shadow-sm transition hover:bg-[#3F5C3B] disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {knowledgeLoading ? "追加中..." : "AIで使えるようにする"}
                          </button>
                        )}
                        <Link
                          to={`/executive-memos/${memo.id}`}
                          className="min-h-[42px] w-full rounded-full border border-[#7C5A4E] bg-white px-4 py-2 text-center text-sm font-black text-[#7C5A4E] shadow-sm transition hover:bg-[#F5EDE8]"
                        >
                          詳細を見る
                        </Link>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          )}

          {nextCursor ? (
            <div className="mt-4 flex justify-center">
              <button
                type="button"
                onClick={() => void loadMemos(appliedFilters, nextCursor)}
                disabled={loadingMore}
                className="min-h-[42px] rounded-full border border-[#D8C0AF] bg-white px-6 py-2.5 text-sm font-black text-[#7C5A4E] transition hover:border-[#A67C52] hover:bg-[#FFF7F2] disabled:opacity-50"
              >
                {loadingMore ? "読み込み中..." : "もっと見る"}
              </button>
            </div>
          ) : null}
        </section>
      </div>
    </main>
  );
}
